import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface TrackImage {
  id: string;
  track_type: 'drag' | 'drift';
  title: string;
  description: string;
  storage_path: string;
  is_featured: boolean;
  is_event_flyer: boolean;
  event_dates?: EventDate[];
  url?: string;
}

interface EventDate {
  id: string;
  event_date: string;
}

interface GalleryMedia {
  id: string;
  title: string;
  description: string;
  storage_path: string;
  media_type: 'image' | 'video';
  is_featured: boolean;
  is_carousel: boolean;
  created_at: string;
  url?: string;
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  ticket_shop_url?: string;
}

function formatDateShort(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function NextEventCountdown({ eventDate }: { eventDate: string }) {
  const diff = (iso: string) => {
    const [y, m, d] = iso.split('-');
    const target = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)).getTime();
    const ms = target - Date.now();
    if (ms < 0) return { d: 0, h: 0, m: 0, s: 0 };
    return {
      d: Math.floor(ms / 86400000),
      h: Math.floor((ms / 3600000) % 24),
      m: Math.floor((ms / 60000) % 60),
      s: Math.floor((ms / 1000) % 60),
    };
  };

  const [t, setT] = useState(() => diff(eventDate));

  useEffect(() => {
    const id = setInterval(() => setT(diff(eventDate)), 1000);
    return () => clearInterval(id);
  }, [eventDate]);

  const box = (val: number, label: string) => (
    <div className="flex-1 text-center">
      <div className="font-display font-black text-3xl sm:text-4xl leading-none tabular-nums">
        {String(val).padStart(2, '0')}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-1.5">{label}</div>
    </div>
  );

  return (
    <div className="flex gap-1">
      {box(t.d, 'days')}
      <span className="text-zinc-600 text-3xl sm:text-4xl leading-none self-start">:</span>
      {box(t.h, 'hrs')}
      <span className="text-zinc-600 text-3xl sm:text-4xl leading-none self-start">:</span>
      {box(t.m, 'min')}
      <span className="text-zinc-600 text-3xl sm:text-4xl leading-none self-start">:</span>
      {box(t.s, 'sec')}
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eventFlyers, setEventFlyers] = useState<TrackImage[]>([]);
  const [carouselMedia, setCarouselMedia] = useState<GalleryMedia[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [allSlides, setAllSlides] = useState<GalleryMedia[]>([]);
  const [events, setEvents] = useState<Event[]>([]);

  useEffect(() => {
    fetchEventFlyers();
    fetchCarouselMedia();
    fetchEvents();
  }, []);

  useEffect(() => {
    setAllSlides(carouselMedia);
    setCurrentSlideIndex(0);
  }, [carouselMedia]);

  useEffect(() => {
    if (allSlides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlideIndex((current) => (current + 1) % allSlides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [allSlides.length]);

  const fetchEventFlyers = async () => {
    try {
      const { data, error } = await supabase
        .from('track_images')
        .select(
          `
          *,
          event_dates (
            id,
            event_date
          )
        `
        )
        .eq('is_event_flyer', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const imagesWithUrls = await Promise.all(
        (data || []).map(async (image) => {
          const {
            data: { publicUrl },
          } = supabase.storage.from('track-images').getPublicUrl(image.storage_path);

          return {
            ...image,
            url: publicUrl,
          };
        })
      );

      setEventFlyers(imagesWithUrls);
    } catch (error) {
      console.error('Error fetching event flyers:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase.from('events').select('id, name, event_date, ticket_shop_url').order('event_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchCarouselMedia = async () => {
    try {
      const { data, error } = await supabase.from('gallery_media').select('*').eq('is_carousel', true).order('created_at', { ascending: false });

      if (error) throw error;

      const itemsWithUrls = await Promise.all(
        (data || []).map(async (item) => {
          const {
            data: { publicUrl },
          } = supabase.storage.from('gallery').getPublicUrl(item.storage_path);

          return {
            ...item,
            url: publicUrl,
          };
        })
      );

      setCarouselMedia(itemsWithUrls);
    } catch (error) {
      console.error('Error fetching carousel media:', error);
    }
  };

  const handleBookClick = () => {
    if (!user) {
      navigate('/login', { state: { from: '/services' } });
    } else {
      navigate('/services');
    }
  };

  const isEventInFuture = (eventDate: string): boolean => {
    if (!eventDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = eventDate.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    return date >= today;
  };

  const getNextEventDate = (eventDates: EventDate[] | undefined): EventDate | null => {
    if (!eventDates || eventDates.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const futureDate = eventDates.find((ed) => {
      const [y, m, d] = ed.event_date.split('-');
      const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      return date >= today;
    });
    return futureDate || null;
  };

  const getTicketShopUrl = (_trackImageId: string, eventDate: string | undefined): string | null => {
    if (!eventDate) return null;
    const event = events.find((e) => e.event_date === eventDate);
    return event?.ticket_shop_url || null;
  };

  const upcomingEventFlyers = eventFlyers.filter((flyer) => {
    if (!flyer.event_dates || flyer.event_dates.length === 0) return false;
    return flyer.event_dates.some((ed) => isEventInFuture(ed.event_date));
  });

  const recentUpcomingEvents = upcomingEventFlyers
    .sort((a, b) => {
      const aNextDate = getNextEventDate(a.event_dates);
      const bNextDate = getNextEventDate(b.event_dates);
      if (!aNextDate) return 1;
      if (!bNextDate) return -1;
      const [aYear, aMonth, aDay] = aNextDate.event_date.split('-');
      const [bYear, bMonth, bDay] = bNextDate.event_date.split('-');
      const aDate = new Date(parseInt(aYear, 10), parseInt(aMonth, 10) - 1, parseInt(aDay, 10));
      const bDate = new Date(parseInt(bYear, 10), parseInt(bMonth, 10) - 1, parseInt(bDay, 10));
      return aDate.getTime() - bDate.getTime();
    })
    .slice(0, 3);

  const nextCalendarEvent = events.find((e) => isEventInFuture(e.event_date));
  const futureEvents = events.filter((e) => isEventInFuture(e.event_date));
  const tickerSource =
    futureEvents.length > 0
      ? futureEvents
      : [{ id: 'placeholder', name: 'New events coming soon', event_date: '', ticket_shop_url: undefined }];
  const tickerLoop = [...tickerSource, ...tickerSource];
  const featuredTiles = carouselMedia.slice(0, 9);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <section className="relative min-h-[calc(100vh-4rem)] overflow-hidden jc-grain">
        {allSlides.length > 0 &&
          allSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlideIndex ? 'opacity-100' : 'opacity-0'}`}
            >
              {slide.media_type === 'image' ? (
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.92) 100%), url(${slide.url})`,
                  }}
                />
              ) : (
                <div className="w-full h-full relative">
                  <video src={slide.url} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/92" />
                </div>
              )}
            </div>
          ))}

        {allSlides.length === 0 && <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-rose-950/30" />}

        <div
          className="absolute top-0 right-0 w-32 sm:w-40 h-full opacity-40 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(244,63,94,0.35), rgba(244,63,94,0))',
            clipPath: 'polygon(60% 0, 100% 0, 100% 100%, 0% 100%)',
          }}
        />

        {/* Ticker */}
        <div className="relative z-[2] border-y border-white/[0.06] bg-black/40 backdrop-blur-md overflow-hidden">
          <div className="flex gap-16 py-2.5 whitespace-nowrap animate-jc-ticker">
            {tickerLoop.map((e, i) => (
              <div key={`${e.id}-${i}`} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.14em]">
                <span className="text-primary-400">●</span>
                <span className="text-white font-semibold">{e.name}</span>
                {e.event_date ? (
                  <span className="text-zinc-400">{formatDateShort(e.event_date)}</span>
                ) : (
                  <span className="text-zinc-500">—</span>
                )}
                <span className="text-zinc-600">—</span>
                <span className="text-zinc-500">Jacky Circuit</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-end">
          <div>
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-10 h-0.5 bg-primary-500" />
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary-400">Motorsport park</span>
            </div>
            <h1 className="font-display font-black uppercase leading-[0.88] text-[clamp(2.75rem,10vw,7rem)] tracking-tight">
              Where
              <br />
              <span className="text-primary-500">seconds</span>
              <br />
              decide.
            </h1>
            <p className="mt-8 text-lg text-zinc-400 max-w-xl leading-relaxed">
              Professional drag strip and drift course. Open to members, teams, and first-time drivers.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button
                type="button"
                onClick={handleBookClick}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm px-6 py-3.5 transition-colors"
              >
                Book the track
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 text-white font-semibold text-sm px-6 py-3.5 transition-colors"
              >
                See the action
              </Link>
              <Link
                to="/tournament-results"
                className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 text-white font-semibold text-sm px-6 py-3.5 transition-colors"
              >
                Leaderboard
              </Link>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-zinc-950/70 backdrop-blur-xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_10px_#22c55e]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-400">Next event</span>
            </div>
            {nextCalendarEvent ? (
              <>
                <div className="font-display text-2xl font-black leading-tight mb-1">{nextCalendarEvent.name}</div>
                <div className="text-zinc-400 text-sm mb-6">
                  {formatDateForDisplay(nextCalendarEvent.event_date)}
                  {nextCalendarEvent.ticket_shop_url && (
                    <a
                      href={nextCalendarEvent.ticket_shop_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-primary-400 font-medium hover:underline"
                    >
                      Get tickets →
                    </a>
                  )}
                </div>
                <div className="py-5 border-y border-zinc-800/80">
                  <NextEventCountdown eventDate={nextCalendarEvent.event_date} />
                </div>
              </>
            ) : (
              <p className="text-zinc-500 text-sm">No upcoming dates listed yet. Follow the gallery for the latest.</p>
            )}
          </div>
        </div>

        {allSlides.length > 1 && (
          <div className="absolute bottom-6 left-0 right-0 z-[2] flex justify-center gap-2">
            {allSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Slide ${index + 1}`}
                onClick={() => setCurrentSlideIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentSlideIndex ? 'bg-primary-500' : 'bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </section>

      {recentUpcomingEvents.length > 0 && (
        <section className="py-24 px-4 sm:px-8 bg-black border-t border-zinc-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary-400 mb-2">· What&apos;s on</div>
                <h2 className="font-display font-black uppercase text-4xl sm:text-6xl leading-none">Upcoming events</h2>
              </div>
              {user ? (
                <Link
                  to="/events"
                  className="inline-flex items-center gap-2 self-start rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 px-5 py-2.5 text-sm font-semibold"
                >
                  Full calendar
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/track-info"
                  className="inline-flex items-center gap-2 self-start rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 px-5 py-2.5 text-sm font-semibold"
                >
                  Track info
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentUpcomingEvents.map((flyer) => {
                const nextEventDate = getNextEventDate(flyer.event_dates);
                const ticketUrl = nextEventDate ? getTicketShopUrl(flyer.id, nextEventDate.event_date) : null;

                const Wrapper = ticketUrl
                  ? ({ children }: { children: React.ReactNode }) => (
                      <a
                        href={ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative bg-zinc-900/80 rounded-xl overflow-hidden block border border-zinc-800 hover:border-zinc-600 transition-colors"
                      >
                        {children}
                      </a>
                    )
                  : ({ children }: { children: React.ReactNode }) => (
                      <div className="group relative bg-zinc-900/80 rounded-xl overflow-hidden border border-zinc-800">{children}</div>
                    );

                return (
                  <Wrapper key={flyer.id}>
                    <div className="aspect-[3/4] relative">
                      <img src={flyer.url} alt={flyer.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                      <div className="absolute top-4 left-4 right-4 flex justify-between items-start gap-2">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                            flyer.track_type === 'drag' ? 'bg-rose-500/20 text-rose-200 border border-rose-500/30' : 'bg-blue-500/20 text-blue-200 border border-blue-500/30'
                          }`}
                        >
                          {flyer.track_type === 'drag' ? 'Drag' : 'Drift'}
                        </span>
                        {nextEventDate && (
                          <div className="bg-black/50 backdrop-blur-md px-2.5 py-1.5 rounded-lg border border-white/10 text-center min-w-[3rem]">
                            <div className="font-display text-lg font-black leading-none">
                              {new Date(
                                parseInt(nextEventDate.event_date.split('-')[0], 10),
                                parseInt(nextEventDate.event_date.split('-')[1], 10) - 1,
                                parseInt(nextEventDate.event_date.split('-')[2], 10)
                              ).getDate()}
                            </div>
                            <div className="font-mono text-[9px] uppercase text-zinc-400 mt-0.5">
                              {new Date(
                                parseInt(nextEventDate.event_date.split('-')[0], 10),
                                parseInt(nextEventDate.event_date.split('-')[1], 10) - 1,
                                parseInt(nextEventDate.event_date.split('-')[2], 10)
                              ).toLocaleDateString('en', { month: 'short' })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{flyer.track_type === 'drag' ? 'Drag strip' : 'Drift course'}</div>
                        <h3 className="font-display text-2xl font-bold leading-tight">{flyer.title}</h3>
                        {ticketUrl && (
                          <div className="mt-3 text-primary-400 text-sm font-semibold flex items-center gap-1">
                            Get tickets <ArrowRight className="w-3.5 h-3.5" />
                          </div>
                        )}
                      </div>
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {carouselMedia.filter((item) => item.is_featured).length > 0 && (
        <section className="py-24 px-4 sm:px-8 bg-black border-t border-zinc-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary-400 mb-2">Featured</div>
                <h2 className="font-display font-black uppercase text-4xl sm:text-6xl leading-none">From the paddock</h2>
                <p className="text-zinc-500 mt-4 max-w-md text-sm sm:text-base leading-relaxed">
                  Latest shots from the strip and drift course. Open the full gallery to browse everything.
                </p>
              </div>
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 px-5 py-2.5 text-sm font-semibold shrink-0"
              >
                Full gallery
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 rounded-sm overflow-hidden border border-zinc-900">
              {carouselMedia
                .filter((item) => item.is_featured)
                .slice(0, 6)
                .map((item) => (
                  <Link key={item.id} to="/gallery" className="group relative aspect-square bg-zinc-950 overflow-hidden">
                    {item.media_type === 'image' ? (
                      <img src={item.url} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    ) : (
                      <video src={item.url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform bg-gradient-to-t from-black/90 to-transparent">
                      <p className="text-xs font-semibold text-white line-clamp-2">{item.title}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      )}

      {featuredTiles.length > 0 && carouselMedia.filter((item) => item.is_featured).length === 0 && (
        <section className="py-24 px-4 sm:px-8 bg-black border-t border-zinc-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary-400 mb-2">@jackycircuit</div>
                <h2 className="font-display font-black uppercase text-4xl sm:text-6xl leading-none">From the paddock</h2>
              </div>
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/10 px-5 py-2.5 text-sm font-semibold shrink-0"
              >
                Full gallery
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 rounded-sm overflow-hidden border border-zinc-900">
              {featuredTiles.map((p) => (
                <Link key={p.id} to="/gallery" className="group relative aspect-square bg-zinc-950 overflow-hidden">
                  {p.media_type === 'image' ? (
                    <img src={p.url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <video src={p.url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                  )}
                  {p.media_type === 'video' && (
                    <div className="absolute top-2 right-2 text-white/90 text-xs font-mono uppercase">Video</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 px-4 sm:px-8 relative overflow-hidden bg-black border-t border-zinc-900">
        <div
          className="absolute top-0 left-0 right-0 h-2 opacity-90"
          style={{
            background: 'repeating-linear-gradient(45deg, #111 0 14px, #f43f5e 14px 28px)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-2 opacity-90"
          style={{
            background: 'repeating-linear-gradient(45deg, #111 0 14px, #f43f5e 14px 28px)',
          }}
        />
        <div className="max-w-7xl mx-auto text-center relative z-[1] py-8">
          <h2 className="font-display font-black uppercase text-[clamp(2.5rem,8vw,5rem)] leading-[0.92]">
            Same track,
            <br />
            New <span className="text-primary-500">story</span>
            <br />
            every run. 
          </h2>
          <p className="text-zinc-500 mt-6 max-w-lg mx-auto text-base">Book a private session, register, or come watch.</p>
          <button
            type="button"
            onClick={handleBookClick}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm px-8 py-4 transition-colors"
          >
            Book the track
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}

function formatDateForDisplay(dateString: string): string {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
